<template lang="pug">
study-result-summary-list.study-result-summary(
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :breadcrumbMoveEvent='breadcrumbMoveEvent',
  :downloadEvent='downloadEvent',
  :resultButtonEvent='resultButtonEvent',
  :studyResultList='studyResultList',
  :lastMinuteCheckResultLinkEvent='lastMinuteCheckResultLinkEvent',
  :resolveResultLinkEvent='resolveResultLinkEvent'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#downloadIconLabel) ダウンロード
  template(#subjectNameHeader) 科目
  template(#sUnitNameHeader) 選択
  template(#resultHeader) テスト結果
  template(#lastMinuteCheckResultHeader) 直前チェックCBT
  template(#resolveResultHeader) 解きなおしCBT
  template(#resultButtonLabel) 結果を見る
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import StudentsApi from '@/mixins/api/StudentsApi'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import StudyResultDetailsApi from '@/mixins/api/StudyResultDetailsApi'
import StudyResultSummaryList from '@/components/templates/manager/history/StudyResultSummaryList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudyResultListType, StudyResultSummarySearchParamsType } from '@/models/studyResultListModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { StudyResultUtils } from '@/utils/StudyResultUtils'
import { StudentManagementUtils } from '@/utils/StudentManagementUtils'

/**
 * 履歴管理・生徒の成績表画面（教室長・講師）
 */
@Component({
  components: {
    StudyResultSummaryList,
  },
})
export default class StudyResultSummary extends Mixins(
  LoginMethods,
  StudentsApi,
  StudyContentsApi,
  StudyResultDetailsApi
) {
  // テスト詳細ID
  private get studyId(): number {
    return Number(this.$route.params.studyId)
  }

  // 生徒ID
  private get studentId(): number {
    return Number(this.$route.params.studentId)
  }

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: '履歴管理',
      location: { name: 'BranchHistoryStudyResultManagement' },
    },
    {
      label: '生徒を選ぶ',
      location: { name: 'BranchHistoryStudyResultStudentSelect', params: { studyId: String(this.studyId) } },
    },
    {
      label: 'さんの成績',
    },
  ]

  // 教室長・講師権限
  private userRole: UserRoles = UserRoles.BRANCH

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_HISTORY

  // テスト結果一覧
  private studyResultList: StudyResultListType[] | null = null

  // 教室管理画面Cookie情報
  private get branchCookie() {
    return this.$bcbtCookies.branchCookie.get() || {}
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // 検索実行
    await this.search()
    // 生徒名を取得
    const apiResponseStudent = await this.getStudentData(this.studentId, this.userRole)
    this.breadcrumbs[2].label =
      StudentManagementUtils.getStudentUserName(apiResponseStudent) + this.breadcrumbs[2].label
    this.$loading.complete()
  }

  // 検索処理
  private async search() {
    // API呼び出し
    const params: StudyResultSummarySearchParamsType = {
      studyId: this.studyId,
      studentId: this.studentId,
    }
    const apiResponse = await this.getStudyResultContents(params, this.userRole)
    this.studyResultList = StudyResultUtils.formatStudyResultList(apiResponse)
  }

  // ダウンロードイベント
  private async downloadEvent() {
    // Cookieから前画面の実施年度選択情報を取得しリクエストパラメータに使用
    if (!this.branchCookie.studyResultManagementSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // pdfデータを取得
      const [data] = await this.downloadStudyResultPdfPersonal(
        Number(this.branchCookie.studyResultManagementSearch.years),
        this.studyId,
        this.studentId,
        this.userRole
      )
      // 別タブでpdfを開く
      window.open(URL.createObjectURL(data), '_blank')
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Branch${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'BranchLogin' })
  }

  // パンくず遷移イベント
  private breadcrumbMoveEvent(breadcrumb: breadcrumbType) {
    if (breadcrumb.location) {
      this.$router.push(breadcrumb.location)
    }
  }

  // 結果ボタンイベント
  private resultButtonEvent(paperTestId: number) {
    // テストIDが存在しない場合は処理をしない
    if (!paperTestId) {
      return
    }

    this.$router.push({
      name: 'BranchHistoryStudyResultDetail',
      params: { studyId: String(this.studyId), studentId: String(this.studentId), paperTestId: String(paperTestId) },
    })
  }

  // 直前チェック結果リンクイベント
  private lastMinuteCheckResultLinkEvent(sUnitId: number) {
    this.$router.push({
      name: 'BranchHistoryStudyResultLastMinuteCheck',
      params: { studyId: String(this.studyId), studentId: String(this.studentId), sUnitId: String(sUnitId) },
    })
  }

  // 解きなおし結果リンクイベント
  private resolveResultLinkEvent(sUnitId: number) {
    this.$router.push({
      name: 'BranchHistoryStudyResultResolve',
      params: { studyId: String(this.studyId), studentId: String(this.studentId), sUnitId: String(sUnitId) },
    })
  }
}
</script>
